import { apsCommands } from "../../shared/dto/aps.commands";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import * as angular from 'angular';
import * as _ from 'lodash';
import AuthDataServiceName, { AuthDataService } from "../auth/authData.service";
import HttpServiceName, { HttpService } from "../http/http.service";
import { Endpoints } from "../../shared/config/constant.module";
import CheckListInstance, { CheckInstance } from "../../models/checkLists/checkListInstance";

let requires = [AuthDataServiceName, HttpServiceName];

export interface CheckListInstancesService {
  deleteCheckListInstance(instance: apsViewModels.CheckListInstanceItem): angular.IPromise<any>;
  saveCheckListInstance(instance: apsCommands.FillOutCheckList): angular.IPromise<any>;
  getCheckListInstance(id: string, plantLocationId: string, scheduledOn: Date):
    angular.IPromise<apsViewModels.CheckListInstanceItem>;
  getFailedChecksForShop(shopId: string):
    angular.IPromise<apsViewModels.CheckListInstanceListItem[]>;
  getFailedChecksForPlant(plantId: string):
    angular.IPromise<apsViewModels.CheckListInstanceListItem[]>;
  getScheduledCheckListInstances(
    plantId: string,
    shopId: string,
    roleId: string,
    checkListView: apsEnums.CheckListView,
    viewAll: boolean): angular.IPromise<apsViewModels.CheckListInstanceItem[]>;
  getCheckListInstancesOnDate(
    shopId: string,
    viewAll: boolean,
    scheduledOn: Date):
    angular.IPromise<apsViewModels.CheckListInstanceItem>;
  getByRole(
    plantId: string,
    shopId: string,
    roleId: string,
    checkListView: apsEnums.CheckListView,
    viewAll: boolean): angular.IPromise<apsViewModels.CheckListInstanceListItem[]>;
  getUnfinishedInstances(plantId: string, shopId: string):
    angular.IPromise<apsViewModels.CheckListInstanceListItem[]>;
  getRecentInstances(plantId: string, shopId: string, roleId: string, viewAll: boolean):
    angular.IPromise<apsViewModels.CheckListInstanceListItem[]>;
  signOff: (command: apsCommands.SignOffCheckListInstance) => angular.IPromise<any>;
  wellKnownCreate(command: apsCommands.WellKnownCheckListCreate): angular.IPromise<any>;
  getWellKnownCheckListInstance(
    shopId: string,
    checkList: apsEnums.WellKnownCheckList,
    scheduledOn: Date): angular.IPromise<apsViewModels.CheckListInstanceListItem>;

  isAuthorizedForCurrentPlant(instance: { roleId: string; allowableRoles: string[]; }): boolean;
  isAuthorizedForPlant(instance: { roleId: string; allowableRoles: string[]; }, plantId: string): boolean;

  updateStatusAndResult(
    instance: {
      status: apsEnums.CheckListStatus,
      result: apsEnums.CheckListResult
    },
    command: apsCommands.FillOutCheckList): void;
}

class CheckListInstancesSvcImpl implements CheckListInstancesService {
  private httpService: HttpService;
  private authDataService: AuthDataService;

  constructor(httpService: HttpService, authDataService: AuthDataService) {
    this.httpService = httpService;
    this.authDataService = authDataService;
  }

  deleteCheckListInstance(instance: apsViewModels.CheckListInstanceItem): angular.IPromise<any> {
    return this.httpService.delete(
      Endpoints.CHECKLIST_INSTANCE + '/' + instance.id, {
      plantLocationId: instance.plantLocationId,
      scheduledOn: instance.scheduledOn
    });
  }

  saveCheckListInstance(instance: apsCommands.FillOutCheckList): angular.IPromise<any> {
    return this.httpService.put(Endpoints.CHECKLIST_INSTANCE, instance);
  }

  getCheckListInstance(
    id: string,
    plantLocationId: string,
    scheduledOn: Date): angular.IPromise<apsViewModels.CheckListInstanceItem> {
    return this.httpService.get<apsViewModels.CheckListInstanceItem>(
      Endpoints.CHECKLIST_INSTANCE + '/' + id, { plantLocationId: plantLocationId, scheduledOn: scheduledOn });
  }

  getWellKnownCheckListInstance(
    shopId: string,
    checkList: apsEnums.WellKnownCheckList,
    scheduledOn: Date): angular.IPromise<apsViewModels.CheckListInstanceListItem> {
    return this.httpService.get<apsViewModels.CheckListInstanceListItem>(
      Endpoints.CHECKLIST_WELLKNOWN, { shopId: shopId, checkList: checkList, scheduledOn: scheduledOn });
  }

  getScheduledCheckListInstances(
    plantId: string,
    shopId: string,
    roleId: string,
    checkListView: apsEnums.CheckListView,
    viewAll: boolean): angular.IPromise<apsViewModels.CheckListInstanceItem[]> {
    return this.httpService.get<apsViewModels.CheckListInstanceItem[]>(
      Endpoints.CHECKLIST_INSTANCE, {
      plantId: plantId,
      shopId: shopId,
      roleId: roleId,
      checkListView: checkListView,
      viewAll: viewAll
    });
  }

  getCheckListInstancesOnDate(shopId: string, viewAll: boolean, scheduledOn: Date): angular.IPromise<apsViewModels.CheckListInstanceItem> {
    return this.httpService.get<apsViewModels.CheckListInstanceItem>(
      Endpoints.CHECKLIST_INSTANCE, {
      shopId: shopId,
      viewAll: viewAll,
      scheduledOn: scheduledOn
    });
  }

  getByRole(plantId: string, shopId: string, roleId: string, checkListView: apsEnums.CheckListView, viewAll: boolean):
    angular.IPromise<apsViewModels.CheckListInstanceListItem[]> {
    return this.httpService.get<apsViewModels.CheckListInstanceListItem[]>(
      Endpoints.CHECKLIST_INSTANCE, {
      plantId: plantId,
      shopId: shopId,
      roleId: roleId,
      checkListView: checkListView,
      viewAll: viewAll || false
    });
  }

  getUnfinishedInstances(plantId: string, shopId: string): angular.IPromise<apsViewModels.CheckListInstanceListItem[]> {
    return this.httpService.get<apsViewModels.CheckListInstanceListItem[]>(
      Endpoints.CHECKLIST_INSTANCE_UNFINISHED, {
      plantId: plantId,
      shopId: shopId
    });
  }

  getRecentInstances(plantId: string, shopId: string, roleId: string, viewAll: boolean):
    angular.IPromise<apsViewModels.CheckListInstanceListItem[]> {
    return this.httpService.get<apsViewModels.CheckListInstanceListItem[]>(
      Endpoints.CHECKLIST_INSTANCE_RECENT, {
      plantId: plantId,
      shopId: shopId,
      roleId: roleId,
      viewAll: viewAll
    });
  }

  getFailedChecksForShop(shopId: string): angular.IPromise<apsViewModels.CheckListInstanceListItem[]> {
    return this.httpService.get<apsViewModels.CheckListInstanceListItem[]>(
      Endpoints.CHECKS_FAILED_FOR_SHOP + shopId);
  }

  getFailedChecksForPlant(plantId: string): angular.IPromise<apsViewModels.CheckListInstanceListItem[]> {
    return this.httpService.get<apsViewModels.CheckListInstanceListItem[]>(
      Endpoints.CHECKS_FAILED_FOR_PLANT + plantId);
  }

  signOff(command: apsCommands.SignOffCheckListInstance) {
    return this.httpService.post(Endpoints.CHECKLIST_INSTANCE_SIGNOFF, command);
  }

  wellKnownCreate(command: apsCommands.WellKnownCheckListCreate) {
    return this.httpService.post(Endpoints.CHECKLIST_WELLKNOWNCREATE, command);
  }

  isAuthorizedForCurrentPlant(instance: { roleId: string; allowableRoles: string[]; }): boolean {
    var loc = this.authDataService.getSelectedRoleLocation();
    return !loc.role.roleRequiresPlants || (loc.plant && this.isAuthorizedForPlant(instance, loc.plant.id));
  }

  isAuthorizedForPlant(instance: { roleId: string; allowableRoles: string[]; }, plantId: string): boolean {
    var currentRole = this.authDataService.getSelectedRoleLocation();
    var role = currentRole.role;
    return this.authDataService.isAuthorizedForPlant(plantId)
      && (this.authDataService.isPlantAdmin(plantId)
        || role.roleId === instance.roleId
        || _.some(instance.allowableRoles, (r) => r === role.roleId));
  }

  updateStatusAndResult(
    instance: { 
      status: apsEnums.CheckListStatus, 
      result: apsEnums.CheckListResult },
    command: apsCommands.FillOutCheckList) {
    if (!command) {
      return;
    }

    var hasFailure = _.some(command.results, (r: apsCommands.FillOutCheckResult) => r.status === apsEnums.CheckStatus.Failed);
    var incomplete = _.some(command.results, (r: apsCommands.FillOutCheckResult) => r.status === apsEnums.CheckStatus.NotDone);
    if (incomplete) {
      if (instance.status < apsEnums.CheckListStatus.NotDone) {
        instance.status = apsEnums.CheckListStatus.Partial;
      }
      if (!hasFailure) {
        instance.result = apsEnums.CheckListResult.Incomplete;
      } else {
        instance.result = apsEnums.CheckListResult.Fail;
      }
    } else {
      instance.status = apsEnums.CheckListStatus.Complete;
      if (!hasFailure) {
        instance.result = apsEnums.CheckListResult.Pass;
      } else {
        instance.result = apsEnums.CheckListResult.Fail;
      }
    }
  }
}

export default angular
  .module('aps.services.checkListInstances', requires)
  .service('checkListInstancesService', CheckListInstancesSvcImpl)
  .name;


// WEBPACK FOOTER //
// ./src/app/services/checkLists/checkListInstances.service.ts